import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, ChevronRight, Calendar } from 'lucide-react';

interface BlogPost {
  slug: string;
  tag: string;
  tagColor: string;
  title: string;
  excerpt: string;
  readTime: string;
  date: string;
  content: React.ReactNode;
}

const posts: BlogPost[] = [
  {
    slug: "ai-quick-wins",
    tag: "Quick Wins",
    tagColor: "bg-blue-100 text-blue-700",
    title: "5 AI Quick Wins Every Business Can Implement This Week",
    excerpt: "You don't need a six-figure AI strategy to start saving time. Here are five automations that deliver immediate ROI for teams of any size.",
    readTime: "5 min read",
    date: "June 2025",
    content: (
      <div className="prose prose-gray max-w-none">
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          The biggest misconception about AI is that you need a large budget, a data science team, or months of planning to see real results. You don't. Some of the highest-impact AI applications are also the simplest  -  and you can start this week.
        </p>

        <h2>1. Automated email drafting</h2>
        <p>
          If your team regularly writes similar emails  -  proposals, follow-ups, customer responses, introductions  -  you're leaving hours on the table every week. AI tools like Claude or ChatGPT can draft high-quality emails in seconds when given a brief prompt. The key is building a simple prompt template for each email type your business sends regularly.
        </p>
        <p>
          <strong>Time saved:</strong> 30–60 minutes per person per day for email-heavy roles.
        </p>

        <h2>2. Meeting summaries and action items</h2>
        <p>
          Tools like Otter.ai, Fireflies, or Microsoft Copilot can transcribe meetings in real time and automatically extract action items, decisions, and key discussion points. No more chasing people for notes or forgetting what was agreed.
        </p>
        <p>
          <strong>Time saved:</strong> 15–30 minutes per meeting, plus dramatically better follow-through on actions.
        </p>

        <h2>3. Document summarisation</h2>
        <p>
          Got long reports, contracts, or briefings that need to be read before a meeting? AI can summarise them to the key points in under 30 seconds. This applies to PDFs, Word docs, web articles, and even lengthy email threads.
        </p>
        <p>
          <strong>Time saved:</strong> Depends on reading volume, but typically 1–3 hours per week for senior staff.
        </p>

        <h2>4. First-draft content creation</h2>
        <p>
          Whether it's a LinkedIn post, a client proposal, a job description, or a social media caption  -  AI can produce a solid first draft in seconds. Your job becomes editing and approving, not writing from scratch. Teams that adopt this typically produce 3–5x more content without increasing headcount.
        </p>
        <p>
          <strong>Time saved:</strong> 1–2 hours per content piece.
        </p>

        <h2>5. FAQ and customer query routing</h2>
        <p>
          If your support inbox, WhatsApp, or website chat receives recurring questions, an AI-powered FAQ bot can handle 40–60% of them automatically  -  without a human in the loop. This is one of the fastest automations to set up and often pays for itself within the first month.
        </p>
        <p>
          <strong>Time saved:</strong> Significant for customer-facing teams  -  often 5–10 hours per week.
        </p>

        <h2>Where to start</h2>
        <p>
          Pick one. The mistake most businesses make is trying to implement everything at once and running into decision paralysis. Choose the quick win that affects the most people on your team and start there.
        </p>
        <p>
          If you're not sure which one is right for your business, <a href="https://calendly.com/georgiahasso/30min" target="_blank" rel="noopener noreferrer">book a free discovery call</a>  -  we'll identify your highest-impact starting point in 30 minutes.
        </p>
      </div>
    ),
  },
  {
    slug: "ai-agents-explained",
    tag: "AI Agents",
    tagColor: "bg-violet-100 text-violet-700",
    title: "What AI Agents Actually Are  -  And Why Your Competitors Are Already Using Them",
    excerpt: "AI agents aren't robots. They're software that can reason, plan, and take actions. Here's what that means for your business in plain English.",
    readTime: "7 min read",
    date: "June 2025",
    content: (
      <div className="prose prose-gray max-w-none">
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          The term "AI agent" gets thrown around constantly, but most articles assume you already know what it means. This one doesn't. Here's a plain-English explanation  -  and why it matters for your business right now.
        </p>

        <h2>What is an AI agent, actually?</h2>
        <p>
          A standard AI tool (like ChatGPT in a chat window) takes your input, generates a response, and stops. An AI agent is different: it can take actions, use tools, make decisions across multiple steps, and complete tasks autonomously  -  not just answer questions.
        </p>
        <p>
          Think of the difference this way: asking ChatGPT "How do I process a refund?" is a standard AI interaction. An AI agent could actually <em>process the refund</em>  -  checking the order in your system, validating the request against your policy, issuing the refund, and sending the customer a confirmation email  -  all without a human touching it.
        </p>

        <h2>What can AI agents do for a business?</h2>
        <p>The most common use cases we see right now:</p>
        <ul>
          <li><strong>Lead qualification:</strong> An agent that reads inbound enquiries, researches the company, scores the lead, and drafts a personalised follow-up email for your sales team to review.</li>
          <li><strong>Internal knowledge retrieval:</strong> An agent that can answer employee questions by searching your internal documents, policies, and databases  -  rather than someone having to ask a colleague.</li>
          <li><strong>Reporting automation:</strong> An agent that pulls data from multiple systems, formats it, and delivers a weekly summary report to your inbox every Monday morning.</li>
          <li><strong>Customer onboarding:</strong> An agent that guides new customers through setup steps, answers their questions, and flags anything that needs human attention.</li>
        </ul>

        <h2>Why are competitors already using them?</h2>
        <p>
          The barrier to building AI agents dropped significantly in 2024 with the release of tools like OpenAI's Assistants API, Anthropic's Claude with tools, and platforms like n8n and Make that allow non-developers to wire agents together. What previously required a software engineer now requires someone who can write a clear brief.
        </p>
        <p>
          Businesses that moved early are now running workflows that would have required 2–3 full-time employees. That's a cost advantage that compounds over time.
        </p>

        <h2>Do I need to be a tech company to use AI agents?</h2>
        <p>
          No. The businesses getting the most value from agents right now are often the least "techy"  -  professional services firms, logistics companies, retail businesses, financial advisors. They have well-defined, repetitive workflows that are perfect for automation.
        </p>
        <p>
          The key is having someone (internally or externally) who can translate your business processes into an agent design. That's exactly what we do at BragAI.
        </p>

        <h2>Where should you start?</h2>
        <p>
          Identify a workflow in your business that has: (1) clear inputs and outputs, (2) defined rules or logic, and (3) a human doing repetitive, low-judgment work. That's your first agent candidate.
        </p>
        <p>
          Start with a discovery conversation  -  we'll map the workflow, tell you what's possible, and give you a realistic picture of what it would take to automate it.
        </p>
      </div>
    ),
  },
  {
    slug: "cost-of-not-adopting-ai",
    tag: "Strategy",
    tagColor: "bg-emerald-100 text-emerald-700",
    title: "The Hidden Cost of Not Adopting AI: How Businesses Are Falling Behind",
    excerpt: "While you're evaluating whether AI is worth it, your competitors are already 12 months ahead. The window to catch up is still open  -  but not forever.",
    readTime: "6 min read",
    date: "June 2025",
    content: (
      <div className="prose prose-gray max-w-none">
        <p className="text-xl text-gray-600 leading-relaxed mb-8">
          Most conversations about AI adoption focus on what you gain. This one is about what you lose by waiting  -  and why the cost of inaction is higher than most business owners realise.
        </p>

        <h2>The compounding advantage problem</h2>
        <p>
          AI adoption creates compounding advantages. A business that automates its proposal process in January is producing twice as many proposals by June, learning from the data, and refining the output. The business that starts in June isn't just six months behind  -  it's behind in accumulated learning, process refinement, and customer relationships won.
        </p>
        <p>
          This isn't theoretical. We're already seeing it happen across industries. Law firms, marketing agencies, financial advisors, and logistics businesses that adopted AI workflows in 2023–2024 have meaningfully lower costs and faster turnaround times than their non-adopting peers.
        </p>

        <h2>The talent gap is widening too</h2>
        <p>
          AI-native employees are increasingly expecting the businesses they join to have modern tools. If your competitors are offering AI-assisted workflows and you're not, you'll start losing talent  -  not to AI, but to the companies that are using it. The best people want to work efficiently.
        </p>

        <h2>The "we're evaluating it" trap</h2>
        <p>
          There's a particular kind of business that reads every article about AI, attends the webinars, and is "exploring options"  -  without ever actually implementing anything. Meanwhile, competitors who spent less time evaluating and more time experimenting are now six to twelve months ahead.
        </p>
        <p>
          You don't need to have a perfect AI strategy to start. You need a starting point. One automation. One process improved. Learn from it and build from there.
        </p>

        <h2>What "falling behind" actually looks like</h2>
        <p>It's rarely dramatic. It looks like:</p>
        <ul>
          <li>Losing a pitch to a competitor who can turn around proposals in 24 hours instead of 72</li>
          <li>A key employee leaving because they spend 60% of their time on work that "should be automated by now"</li>
          <li>Customers asking a competitor's AI chatbot for recommendations and being steered away from your business</li>
          <li>Carrying headcount for work that your competitors have automated</li>
        </ul>

        <h2>The window is still open</h2>
        <p>
          The good news: most industries are still in early adoption. Most businesses haven't moved beyond basic tools. If you start now, you're not late  -  you're on time.
        </p>
        <p>
          But the window for "catching up easily" won't stay open indefinitely. The businesses building AI workflows today will have a structural advantage in 2026 and 2027 that will be difficult to close quickly.
        </p>

        <h2>The practical next step</h2>
        <p>
          You don't need a six-month strategy process. You need a 30-minute conversation to identify your first AI opportunity. From there, we can have something live in two to four weeks.
        </p>
        <p>
          <a href="https://calendly.com/georgiahasso/30min" target="_blank" rel="noopener noreferrer">Book a free discovery call</a>  -  let's figure out your starting point together.
        </p>
      </div>
    ),
  },
];

const BlogPage = () => {
  const [activePost, setActivePost] = useState<string | null>(null);

  const currentPost = posts.find(p => p.slug === activePost);

  if (currentPost) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                BragAI
              </Link>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              >
                Book Discovery Call
              </button>
            </div>
          </div>
        </nav>

        <div className="pt-28 pb-24 px-6">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setActivePost(null)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to all posts
            </button>

            <div className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mb-5 ${currentPost.tagColor}`}>
              {currentPost.tag}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-5">
              {currentPost.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-400 text-sm mb-10 pb-10 border-b border-gray-100">
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {currentPost.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {currentPost.readTime}</span>
            </div>

            <div className="prose-headings:font-bold prose-headings:text-gray-900 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-5 prose-ul:text-gray-600 prose-ul:space-y-2 prose-li:leading-relaxed prose-a:text-blue-600 prose-a:underline prose-strong:text-gray-800">
              {currentPost.content}
            </div>

            <div className="mt-16 p-8 bg-blue-50 rounded-2xl border border-blue-100">
              <h3 className="font-bold text-gray-900 text-xl mb-3">Ready to implement AI in your business?</h3>
              <p className="text-gray-600 mb-6">Start with a free 30-minute discovery call. We'll identify your highest-impact AI opportunity.</p>
              <button
                onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3 rounded-lg font-semibold transition-colors"
              >
                Book Free Discovery Call
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              BragAI
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Home</Link>
              <a href="/#services" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Services</a>
              <a href="/#contact" className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium">Contact</a>
            </div>
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            >
              Book Discovery Call
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-8 w-fit transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Blog</p>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">AI in the Workplace</h1>
            <p className="text-xl text-gray-500 max-w-2xl">
              Practical insights on implementing AI in your business  -  no hype, no jargon.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((post) => (
              <button
                key={post.slug}
                onClick={() => setActivePost(post.slug)}
                className="group text-left bg-white rounded-2xl p-7 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
              >
                <div className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold mb-4 ${post.tagColor}`}>
                  {post.tag}
                </div>
                <h2 className="text-gray-900 font-bold text-lg leading-snug mb-3 group-hover:text-blue-600 transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-gray-400 text-xs">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-20 p-10 bg-blue-50 rounded-3xl border border-blue-100 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Want personalised AI advice for your business?</h2>
            <p className="text-gray-600 mb-7 max-w-xl mx-auto">Book a free discovery call and we'll identify your highest-impact AI opportunities in 30 minutes.</p>
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors shadow-sm"
            >
              Book Free Discovery Call
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
